import { faker } from '@faker-js/faker';
import { toISODateString } from 'date-util';
import dayjs from 'dayjs';
import { HttpResponse, delay, http } from 'msw';
import { FIXED_REF_DATE } from '../../constants';

const mockOfficeMembersMap: Record<string, string> = {
  'member-1': '山田 太郎',
  'member-2': '佐藤 花子',
  'member-3': '鈴木 一郎',
  'member-4': '田中 次郎',
  'member-5': '伊藤 美咲',
};

const mockOfficeMemberIds = Object.keys(mockOfficeMembersMap);

/** ID for mock: cancel returns CommonError 422 (for error handling check) */
export const MOCK_CANCEL_ERROR_ID = 'trigger-cancel-error';

/** Error messages returned by cancel API when id = MOCK_CANCEL_ERROR_ID. UI shows these joined by newline. */
export const MOCK_CANCEL_ERROR_MESSAGES = [
  'モック用エラーメッセージ1',
  'モック用エラーメッセージ2',
] as const;

const importExportHistoriesBase = Array.from(
  { length: 400 },
  (_, i) => i + 1
).map(() => {
  const type = faker.helpers.arrayElement(['import', 'export'] as const);
  const officeMemberId = faker.helpers.arrayElement(mockOfficeMemberIds);

  // Generate file name with varying lengths
  let fileName: string | undefined;
  if (type === 'import') {
    const fileNameType = faker.helpers.arrayElement([
      'short',
      'medium',
      'long',
      'very_long',
      'japanese_long',
    ]);

    switch (fileNameType) {
      case 'short':
        fileName = faker.system.fileName();
        break;
      case 'medium':
        fileName = `${faker.lorem.words(3).replace(/\s/g, '_')}_${faker.date
          .past()
          .getTime()}.csv`;
        break;
      case 'long':
        fileName = `${faker.lorem.words(8).replace(/\s/g, '_')}_${faker.date
          .past()
          .getTime()}.csv`;
        break;
      case 'very_long':
        fileName = `very_long_file_name_with_many_characters_to_test_truncation_functionality_in_the_table_cell_component_for_proper_display_${faker.date
          .past()
          .getTime()}.csv`;
        break;
      case 'japanese_long':
        fileName =
          'これは日本語の非常に長いファイル名でテーブルセルでのファイル名のトランケーション機能をテストするためのものです.csv';
        break;
    }
  }

  return {
    id: faker.number.int({ min: 1, max: 1000000 }).toString(),
    office_member_id: officeMemberId,
    can_download: faker.datatype.boolean(),
    completed_at: faker.date.anytime().toISOString(),
    error_message: '',
    executed_at: faker.date.anytime().toISOString(),
    import_file_byte_size:
      type === 'import'
        ? faker.number.int({ min: 1, max: 1000000 })
        : undefined,
    file_name: fileName,
    source: faker.helpers.arrayElement([
      'custom_transaction_item_options',
    ] as const),
    status: faker.helpers.arrayElement([
      'processing',
      'completed',
      'failed',
      'canceled',
    ] as const),
    submitted_at: faker.date.anytime().toISOString(),
    executed_by: mockOfficeMembersMap[officeMemberId],
    type: type,
  };
});

/** Row that triggers cancel error in mock (id = MOCK_CANCEL_ERROR_ID). Reused for prepending to list. */
const MOCK_CANCEL_ERROR_ROW = {
  id: MOCK_CANCEL_ERROR_ID,
  office_member_id: 'member-1',
  can_download: false,
  completed_at: '',
  error_message: '',
  executed_at: new Date().toISOString(),
  import_file_byte_size: 1000,
  file_name: 'mock-error-test.csv',
  source: 'custom_transaction_item_options' as const,
  status: 'processing' as const,
  submitted_at: new Date().toISOString(),
  executed_by: mockOfficeMembersMap['member-1'],
  type: 'import' as const,
};

/** Prepend a row that triggers cancel error in mock (id = MOCK_CANCEL_ERROR_ID) */
const importExportHistories = [
  MOCK_CANCEL_ERROR_ROW,
  ...importExportHistoriesBase,
];

type searchQuery = {
  type?: string;
  source?: string;
  office_member_ids?: string[];
  submitted_at_to?: string;
  submitted_at_from?: string;
  page?: number;
  per_page?: number;
  sort_by?: string;
};

const isValidSortBy = (
  sortBy: string | undefined
): sortBy is
  | 'submitted_at:asc'
  | 'submitted_at:desc'
  | 'completed_at:asc'
  | 'completed_at:desc' => {
  return (
    sortBy === 'submitted_at:asc' ||
    sortBy === 'submitted_at:desc' ||
    sortBy === 'completed_at:asc' ||
    sortBy === 'completed_at:desc'
  );
};

const filteredImportExportHistories = ({
  type,
  source,
  office_member_ids,
  submitted_at_to,
  submitted_at_from,
  sort_by,
}: searchQuery) => {
  const filtered = importExportHistories.filter((history) => {
    const matchesType = type ? history.type === type : true;
    const matchesSource = source ? history.source === source : true;
    const matchesOfficeMemberId =
      office_member_ids && office_member_ids.length > 0
        ? office_member_ids.includes(history.office_member_id)
        : true;
    const matchesSubmittedAtTo = submitted_at_to
      ? new Date(history.submitted_at) <= new Date(submitted_at_to)
      : true;
    const matchesSubmittedAtFrom = submitted_at_from
      ? new Date(history.submitted_at) >= new Date(submitted_at_from)
      : true;

    return (
      matchesType &&
      matchesSource &&
      matchesOfficeMemberId &&
      matchesSubmittedAtTo &&
      matchesSubmittedAtFrom
    );
  });

  if (isValidSortBy(sort_by)) {
    const [field, order] = sort_by.split(':');

    filtered.sort((a, b) => {
      const aValue =
        field === 'submitted_at'
          ? a.submitted_at
          : field === 'completed_at'
          ? a.completed_at
          : '';
      const bValue =
        field === 'submitted_at'
          ? b.submitted_at
          : field === 'completed_at'
          ? b.completed_at
          : '';

      if (!aValue && !bValue) return 0;
      if (!aValue) return 1;
      if (!bValue) return -1;

      const comparison =
        new Date(aValue).getTime() - new Date(bValue).getTime();
      return order === 'asc' ? comparison : -comparison;
    });
  }

  // Ensure cancel-error row is always first so it appears on page 1 (for manual testing)
  const triggerIndex = filtered.findIndex((h) => h.id === MOCK_CANCEL_ERROR_ID);
  if (triggerIndex > 0) {
    const [trigger] = filtered.splice(triggerIndex, 1);
    if (trigger !== undefined) filtered.unshift(trigger);
  }

  return filtered;
};

export const getImportExportHistories = ({
  page,
  per_page = 50,
  sort_by,
  type,
  source,
  office_member_ids,
  submitted_at_to,
  submitted_at_from,
}: searchQuery) => {
  const importExportHistories = filteredImportExportHistories({
    sort_by,
    type,
    source,
    office_member_ids,
    submitted_at_to,
    submitted_at_from,
  });
  const totalItems = importExportHistories.length;
  const start = (page ? page - 1 : 0) * per_page;
  const end = start + per_page;
  const paginatedItems = importExportHistories.slice(start, end);

  const totalPages =
    type ||
    source ||
    (office_member_ids && office_member_ids.length > 0) ||
    submitted_at_to ||
    submitted_at_from
      ? Math.ceil(importExportHistories.length / per_page)
      : Math.ceil(totalItems / per_page);
  return {
    import_export_histories: paginatedItems,
    pagination: {
      has_next_page: (page ? page - 1 : 0) < totalPages,
      has_previous_page: start > 0,
      page,
      per_page,
      total_pages: totalPages,
      total_records: totalItems,
    },
  };
};

export const getGetImportHistoryErrorsMock = () => ({
  submitted_at: toISODateString(
    dayjs(faker.date.past({ refDate: FIXED_REF_DATE }))
  ),
  completed_at: toISODateString(
    dayjs(faker.date.past({ refDate: FIXED_REF_DATE }))
  ),
  import_error_messages: Array.from(
    { length: faker.number.int({ min: 10, max: 100 }) },
    (_, i) => i + 1
  ).map(() => ({
    messages: Array.from(
      { length: faker.number.int({ min: 1, max: 10 }) },
      (_, i) => i + 1
    ).map(() => faker.word.sample()),
    row: faker.number.int({ min: undefined, max: undefined }),
  })),
});

export const handlers = [
  http.get('*/api/js/import_export_histories', async ({ request }) => {
    await delay(1000);
    const url = new URL(request.url);
    const page = url.searchParams.has('page')
      ? parseInt(url.searchParams.get('page') ?? '1')
      : 1;
    const type = url.searchParams.get('type');
    const source = url.searchParams.get('source');
    const officeMemberIds = url.searchParams.getAll('office_member_ids[]');
    const submitted_at_to = url.searchParams.get('submitted_at_to');
    const submitted_at_from = url.searchParams.get('submitted_at_from');
    const sort_by = url.searchParams.get('sort_by');

    return new HttpResponse(
      JSON.stringify(
        getImportExportHistories({
          page,
          type: type ?? '',
          source: source ?? '',
          office_member_ids:
            officeMemberIds.length > 0 ? officeMemberIds : undefined,
          submitted_at_to: submitted_at_to ?? '',
          submitted_at_from: submitted_at_from ?? '',
          sort_by: sort_by ?? undefined,
        })
      ),
      {
        status: 200,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
  }),
  http.get('*/api/js/import_export_histories/sources', async () => {
    await delay(1000);
    return new HttpResponse(
      JSON.stringify({
        sources: [
          {
            display_name: 'カスタム取引項目オプション',
            value: 'custom_transaction_item_options',
          },
        ],
      }),
      {
        status: 200,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
  }),
  http.post(
    '*/api/js/import_export_histories/:id/cancel',
    async ({ params }) => {
      await delay(300);
      if (params.id === MOCK_CANCEL_ERROR_ID) {
        return new HttpResponse(
          JSON.stringify({
            messages: [...MOCK_CANCEL_ERROR_MESSAGES],
            code: null,
          }),
          {
            status: 422,
            headers: { 'Content-Type': 'application/json' },
          }
        );
      }
      return new HttpResponse(null, { status: 200 });
    }
  ),
  http.get('*/api/js/import_export_histories/:id/errors', async () => {
    const mock = getGetImportHistoryErrorsMock();
    return new HttpResponse(JSON.stringify(mock), {
      status: 200,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }),
];
